import { browserEffectRuntime } from "@crm/effect/client/runtime";

/**
 * Knowledge Pool Page (Spec 059)
 *
 * 认领池：浏览已审核条目，认领编写文章
 * 支持客户端搜索 + 分页
 */

import { InfiniteScrollTrigger, ListPageShell, PageHeader } from "@crm/ui/components/shared";
import { QueryFailureCard } from "@crm/ui/components/shared/query-failure-card";
import { Badge } from "@crm/ui/components/ui/badge";
import { Button } from "@crm/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@crm/ui/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@crm/ui/components/ui/dialog";
import { SearchInput } from "@crm/ui/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@crm/ui/components/ui/select";
import { useDebounce } from "@crm/ui/hooks/use-debounce";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Data, Effect, Option } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import { Eye, Hand } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useClaimKnowledgeEntry } from "#app/knowledge/knowledge.mutations";
import {
  useKnowledgeEntriesInfinite,
  useKnowledgeEntryTags,
  useKnowledgeTags,
} from "#app/knowledge/knowledge.queries";
import { KnowledgeNav } from "#components/knowledge";

class KnowledgePoolRouteWorkflowError extends Data.TaggedError("KnowledgePoolRouteWorkflowError")<{
  readonly message: string;
  readonly cause?: unknown;
}> {}

function toKnowledgePoolRouteWorkflowError(
  cause: unknown,
  fallbackMessage: string,
): KnowledgePoolRouteWorkflowError {
  return new KnowledgePoolRouteWorkflowError({
    message: cause instanceof Error ? cause.message : fallbackMessage,
    cause,
  });
}

function promiseKnowledgePoolRouteEffect<A>(
  run: () => PromiseLike<A>,
  fallbackMessage: string,
): Effect.Effect<A, KnowledgePoolRouteWorkflowError> {
  return Effect.tryPromise({
    try: run,
    catch: (cause) => toKnowledgePoolRouteWorkflowError(cause, fallbackMessage),
  });
}

export const Route = createFileRoute("/_protected/knowledge/pool")({
  head: () => ({
  }),
  component: PoolPage,
});

function PoolPage() {
  const navigate = useNavigate();
  const [selectedTag, setSelectedTag] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [previewEntry, setPreviewEntry] = useState<{
    id: string;
    title: string;
    sourceMessage: string | null;
  } | null>(null);

  const debouncedSearch = useDebounce(searchTerm, 300);

  // 认领 mutation
  const claimEntryMutation = useClaimKnowledgeEntry();

  // 获取待认领条目（服务端分页 + infinite scroll）
  const {
    items: approvedEntries,
    totalCount,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useKnowledgeEntriesInfinite({
    status: "approved",
    search: debouncedSearch || undefined,
    tagId: selectedTag !== "all" ? selectedTag : undefined,
  });

  // 获取标签
  const tagsResult = useKnowledgeTags();
  const tags = AsyncResult.value(tagsResult).pipe(Option.getOrElse(() => []));

  // 获取条目-标签关联
  const entryTagsResult = useKnowledgeEntryTags();
  const entryTags = AsyncResult.value(entryTagsResult).pipe(Option.getOrElse(() => []));

  // 搜索/筛选变化时自动重置（由 useInfiniteQuery 处理）
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleTagChange = (value: string) => {
    setSelectedTag(value);
  };

  // 获取条目的标签
  const getEntryTags = (entryId: string) => {
    const tagIds = entryTags.filter((et) => et.entryId === entryId).map((et) => et.tagId);
    return tags.filter((t) => tagIds.includes(t.id));
  };

  const handleClaim = (entryId: string) =>
    browserEffectRuntime.runPromise(
      promiseKnowledgePoolRouteEffect(
        () => claimEntryMutation.executePromise(entryId),
        "认领失败",
      ).pipe(
        Effect.tap(() =>
          Effect.sync(() => {
            toast.success("认领成功，正在跳转到编辑页...");
            // 跳转到编辑页
            void navigate({ to: "/knowledge/edit/$id", params: { id: entryId } });
          }),
        ),
        Effect.catch(() => Effect.sync(() => toast.error("认领失败"))),
        Effect.asVoid,
      ),
    );

  if (error !== null) {
    return (
      <ListPageShell>
        <KnowledgeNav />
        <div className="mt-4">
          <PageHeader description="浏览已审核的条目，认领感兴趣的话题编写文章" title="认领池" />
        </div>
        <QueryFailureCard
          className="mt-6"
          description="认领池暂时无法获取，失败不会显示为暂无待认领条目。"
          onRetry={refetch}
          title="认领池加载失败"
        />
      </ListPageShell>
    );
  }

  return (
    <ListPageShell>
      <KnowledgeNav />
      <div className="mt-4">
        <PageHeader description="浏览已审核的条目，认领感兴趣的话题编写文章" title="认领池" />
      </div>

      {/* 搜索 + 标签筛选 */}
      <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <SearchInput
          className="w-full sm:w-64"
          onChange={handleSearchChange}
          placeholder="搜索条目..."
          value={searchTerm}
        />
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">标签:</span>
          <Select onValueChange={handleTagChange} value={selectedTag}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="选择标签" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              {tags.map((tag) => (
                <SelectItem key={tag.id} value={tag.id}>
                  {tag.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {approvedEntries.length === 0 && !isLoading ? (
        <div className="mt-8 py-12 text-center text-muted-foreground">
          <p>暂无待认领的条目</p>
          <Link className="mt-2 inline-block text-primary hover:underline" to="/knowledge">
            返回知识库
          </Link>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {approvedEntries.map((entry) => {
              const entryTagList = getEntryTags(entry.id);
              return (
                <Card className="flex flex-col" key={entry.id}>
                  <CardHeader>
                    <CardTitle className="line-clamp-2 text-lg">{entry.title}</CardTitle>
                    <CardDescription className="flex flex-wrap gap-1">
                      {entryTagList.map((tag) => (
                        <Badge key={tag.id} variant="secondary">
                          {tag.name}
                        </Badge>
                      ))}
                      {entryTagList.length === 0 && (
                        <span className="text-muted-foreground text-xs">暂无标签</span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1">
                    {entry.sourceMessage && (
                      <p className="line-clamp-4 text-muted-foreground text-sm">
                        {entry.sourceMessage}
                      </p>
                    )}
                  </CardContent>
                  <CardFooter className="flex gap-2">
                    <Button onClick={() => setPreviewEntry(entry)} size="sm" variant="outline">
                      <Eye className="mr-1 h-4 w-4" />
                      查看
                    </Button>
                    <Button onClick={() => handleClaim(entry.id)} size="sm" variant="default">
                      <Hand className="mr-1 h-4 w-4" />
                      认领
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>

          {/* Infinite scroll 触发器 */}
          <InfiniteScrollTrigger
            completedText={`已加载全部 ${totalCount} 条待认领条目`}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage ?? false}
            isFetchingNextPage={isFetchingNextPage}
            loadedCount={approvedEntries.length}
            totalCount={totalCount}
          />
        </>
      )}

      {/* 预览对话框 */}
      <Dialog onOpenChange={() => setPreviewEntry(null)} open={!!previewEntry}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewEntry?.title}</DialogTitle>
            <DialogDescription>条目详情</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <h4 className="mb-2 font-medium">来源消息:</h4>
            <div className="whitespace-pre-wrap rounded-md bg-muted p-4 text-sm">
              {previewEntry?.sourceMessage || "无原始消息"}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ListPageShell>
  );
}
